import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check, ChevronDown } from 'lucide-react';
import { ClientSitePreview } from '../components/ClientSitePreview';
import { WebsitePackage } from '../components/WebsitePackage';
import { SeoHead } from '../components/SeoHead';
import { getLocalBusinessSchema } from '../seo/schema';

const selectedWork = [
  {
    name: 'Solariums Direct',
    type: 'Custom home-improvement website',
    url: 'https://solariumsdirect.com/',
    image: '/demos/solariumsdirect',
    summary: 'A complex custom-kit offer organized around education, project examples, and a visible quote path.',
  },
  {
    name: 'Anderson Leadership Foundation',
    type: 'Nonprofit leadership website',
    url: 'https://wisdomchain.org/',
    image: '/demos/wisdomchain',
    summary: 'A leadership-support organization presented through a clear mission, program context, and a direct path to its work.',
  },
  {
    name: 'OrriaDeskAI',
    type: 'SaaS launch website',
    url: 'https://orriadeskai.com/',
    image: '/demos/orriadeskai',
    summary: 'A new software offer given a clear product story, integration context, and demo route.',
  },
  {
    name: 'PAX Learning Center',
    type: 'Education website',
    url: 'https://paxlc.org/',
    image: '/demos/pax',
    summary: 'A high-consideration education choice explained through program details, student support, and a direct application path.',
  },
];

const leadPath = [
  {
    title: 'Website',
    heading: 'A clear front door.',
    copy: 'The right visitor understands what you do, sees credible work, and knows whether to call, request a quote, or book.',
    link: '/services/web-design',
  },
  {
    title: 'Response',
    heading: 'A reply with an owner.',
    copy: 'A new form or call reaches the right place. The customer gets an approved response while your team knows who moves it.',
    link: '/services/workflows',
  },
  {
    title: 'Follow-through',
    heading: 'A visible next step.',
    copy: 'Questions, estimates, booking, and reminders stay attached to the same inquiry until someone closes the loop.',
    link: '/services/workflows',
  },
];

const processSteps = [
  ['Talk for 10 minutes', 'Tell me what you do, which projects you want to show, and when you want to start. We’ll confirm the budget and fit.'],
  ['See your free homepage demo', 'If the project fits, I’ll prepare one homepage direction for your business. We’ll walk through it together before you decide.'],
  ['Approve the scope, then build', 'For the five-page package, a $497.50 deposit starts the full build after scope approval. We test the site and agree the launch milestone.'],
];

const faqs = [
  {
    q: 'Are you a web design company or a CRM company?',
    a: 'The website comes first. We connect the response and follow-up behind it when the current handoff loses inquiries or leaves your team guessing. You get one builder responsible for the customer-facing page and the route behind it.',
  },
  {
    q: 'Do I need to replace the tools I already use?',
    a: 'We start with your current website, phone, inbox, calendar, and CRM. Our conversation helps identify which pieces can stay and what the website needs to connect to.',
  },
  {
    q: 'What do I get from the free homepage demo?',
    a: 'After a short call to confirm the fit, I’ll create one visual homepage direction for your business. We review it together. There is no payment for the demo and no obligation to proceed.',
  },
  {
    q: 'Can I inspect the work before we talk?',
    a: 'Yes. Every website named on this page links to a published client site. We share performance claims only when measurement and client permission support them.',
  },
];

export function Home() {
  const reduceMotion = useReducedMotion();
  const [featured, ...supporting] = selectedWork;

  return (
    <div className="shoreline-home">
      <SeoHead
        title="Custom Business Websites & Free Homepage Demo | Verno Web"
        description="Custom websites built directly with Nate. Five-page build for $995 with basic hosting included. See real work and request a free homepage demo. Domain costs separate."
        path="/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(getLocalBusinessSchema()) }} />

      <section className="shoreline-hero" aria-labelledby="shoreline-hero-heading">
        <div className="shoreline-hero__copy">
          <div>
            <h1 id="shoreline-hero-heading">A better website for the business you’ve built.</h1>
            <p>I build clear, custom websites that show your work and make it easy to get in touch. Start with a free homepage demo after a short call.</p>
            <Link className="shoreline-button" to="/free-demo">Request a free homepage demo <ArrowRight aria-hidden="true" /></Link>
            <span className="shoreline-local"><i aria-hidden="true" />Seattle-based <b>·</b> Built directly with Nate</span>
          </div>
        </div>

        <div className="shoreline-hero__scene">
          <div className="shoreline-hero__fog" aria-hidden="true" />
          <motion.a
            className="shoreline-featured"
            href={featured.url}
            target="_blank"
            rel="noreferrer"
            initial={reduceMotion ? false : { opacity: 0.78, y: 30, filter: 'blur(5px)', clipPath: 'inset(9% 0 0 0)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)', clipPath: 'inset(0% 0 0 0)' }}
            transition={{ duration: 0.78, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            aria-label="Open the Solariums Direct website in a new tab"
          >
            <ClientSitePreview image={featured.image} title={featured.name} url={featured.url} sizes="(min-width: 1100px) 45vw, 88vw" loading="eager" className="shoreline-browser" />
            <div className="shoreline-featured__caption">
              <div><small>Featured client website</small><strong>{featured.name}</strong></div>
              <span>Custom website for Washington and Oregon homeowners.</span>
              <ArrowUpRight aria-hidden="true" />
            </div>
          </motion.a>
          <a className="shoreline-photo-credit" href="https://unsplash.com/photos/forest-lake-near-on-mountain-4Pzn4SBYKIg" target="_blank" rel="noreferrer">Snoqualmie, WA · Photo by Dave Hoefler</a>
        </div>
      </section>

      <section className="shoreline-published" id="work" aria-labelledby="shoreline-published-heading">
        <div>
          <h2 id="shoreline-published-heading">Real sites for real businesses.</h2>
          <p>Published work you can open and judge yourself.</p>
        </div>
        <nav aria-label="More published client websites">
          {supporting.map((work) => <a key={work.name} href={work.url} target="_blank" rel="noreferrer">{work.name}<ArrowUpRight aria-hidden="true" /></a>)}
        </nav>
      </section>

      <WebsitePackage />

      <section className="shoreline-system" aria-labelledby="shoreline-system-heading">
        <div className="shoreline-wrap shoreline-system__intro">
          <h2 id="shoreline-system-heading">The website is the front door. The handoff behind it matters too.</h2>
          <p>Each part has one job: earn the inquiry, answer it, and make the next action obvious to both the customer and your team.</p>
        </div>
        <div className="shoreline-wrap shoreline-system__rows">
          {leadPath.map((part) => (
            <Link to={part.link} key={part.title}>
              <span>{part.title}</span>
              <h3>{part.heading}</h3>
              <p>{part.copy}</p>
              <ArrowRight aria-hidden="true" />
            </Link>
          ))}
        </div>
      </section>

      <section className="shoreline-field-note" aria-labelledby="shoreline-field-note-heading">
        <div className="shoreline-field-note__image">
          <picture>
            <source srcSet="/pnw-north-cascades-900w.webp 900w, /pnw-north-cascades-1800w.webp 1800w" sizes="(min-width: 900px) 50vw, 100vw" type="image/webp" />
            <img src="/pnw-north-cascades.jpg" alt="Douglas firs disappearing into fog in the North Cascades" width="1800" height="2700" loading="lazy" />
          </picture>
          <a href="https://unsplash.com/photos/a-forest-filled-with-lots-of-trees-covered-in-fog-OXMs_I2R2DE" target="_blank" rel="noreferrer">North Cascades, WA · Photo by Joel Swick</a>
        </div>
        <div className="shoreline-field-note__copy">
          <div>
            <span>Built in the Northwest</span>
            <h2 id="shoreline-field-note-heading">Close enough to understand the context. Small enough to stay accountable.</h2>
            <p>Verno is based in Seattle. You work directly with the person shaping the website, mapping the handoff, and answering when something needs attention.</p>
            <Link className="shoreline-text-link" to="/about">How Verno works <ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="shoreline-work" aria-labelledby="shoreline-work-heading">
        <div className="shoreline-wrap">
          <header>
            <h2 id="shoreline-work-heading">Open the work. See how each business is explained.</h2>
            <p>Explore a few of the client websites I’ve designed and built.</p>
          </header>
          <div className="shoreline-work__gallery">
            {supporting.map((work) => (
              <a key={work.name} href={work.url} target="_blank" rel="noreferrer">
                <div className="shoreline-work__image">
                  <ClientSitePreview image={work.image} title={work.name} url={work.url} sizes="(min-width: 900px) 82vw, 100vw" />
                </div>
                <div className="shoreline-work__caption">
                  <div><h3>{work.name}</h3><span>{work.type}</span></div>
                  <p>{work.summary}</p>
                  <ArrowUpRight aria-hidden="true" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="shoreline-process" id="process" aria-labelledby="shoreline-process-heading">
        <div className="shoreline-wrap shoreline-process__grid">
          <div>
            <h2 id="shoreline-process-heading">See your homepage before you commit to the build.</h2>
            <p>A short conversation, a free first look, and a written scope before any payment.</p>
            <Link className="shoreline-text-link" to="/free-demo">Request a free homepage demo <ArrowRight aria-hidden="true" /></Link>
          </div>
          <ol>
            {processSteps.map(([title, copy], index) => (
              <li key={title}><span>0{index + 1}</span><div><h3>{title}</h3><p>{copy}</p></div></li>
            ))}
          </ol>
        </div>
      </section>

      <section className="shoreline-faq" aria-labelledby="shoreline-faq-heading">
        <div className="shoreline-wrap shoreline-faq__grid">
          <div><h2 id="shoreline-faq-heading">Questions worth settling before you hire anyone.</h2><p>Clear ownership matters more than a long feature list.</p></div>
          <div>{faqs.map((faq) => <Faq key={faq.q} {...faq} reduceMotion={reduceMotion} />)}</div>
        </div>
      </section>

      <section className="shoreline-close" aria-labelledby="shoreline-close-heading">
        <div className="shoreline-wrap">
          <div>
            <h2 id="shoreline-close-heading">Let’s see what your next website could look like.</h2>
            <p>Tell me about your business. We’ll have a quick call and, if the project fits, I’ll put together your free homepage demo.</p>
          </div>
          <div className="shoreline-close__action">
            <ul>
              <li><Check aria-hidden="true" />No payment</li>
              <li><Check aria-hidden="true" />A 10-minute call</li>
              <li><Check aria-hidden="true" />A free first look</li>
            </ul>
            <Link className="shoreline-button shoreline-button--light" to="/free-demo">Request a free homepage demo <ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Faq({ q, a, reduceMotion }: { q: string; a: string; reduceMotion: boolean | null }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="shoreline-faq__item">
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{q}</span><ChevronDown className={open ? 'is-open' : ''} aria-hidden="true" />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.24, ease: [0.16, 1, 0.3, 1] }}
          >
            <p>{a}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
